import React, { useState, useEffect } from "react";
import {
  Scatter,
  ScatterChart,
  YAxis,
  XAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, Form, Button, Col, Row, Table } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { format, differenceInHours } from "date-fns";
import { TrashFill } from "react-bootstrap-icons";
import config from "./../services/config";
import { MeasurementService } from "./../services/measurementService";

const calculateReverseTicks = (timestampMs) => {
  // Convert milliseconds to .NET DateTime ticks (100-nanosecond intervals since 0001-01-01)
  const dotNetTicks = timestampMs * 10000 + 621355968000000000;
  const maxTicks = 3155378975999999999; // DateTime.MaxValue.Ticks in .NET
  const reverseTicks = maxTicks - dotNetTicks;
  return reverseTicks.toString();
};

const Metingen = (props) => {
  const { register, handleSubmit, errors, reset, getValues } = useForm();
  const [metingen, setMetingen] = useState([]);

  useEffect(() => {
    loadMeasurements();
  }, []);

  async function loadMeasurements() {
    try {
      const measurements = await MeasurementService.getLast10Measurements();
      setMetingen(measurements);
    } catch (error) {
      console.error("Error loading measurements:", error);
      alert("Kon metingen niet laden. Probeer het opnieuw.");
    }
  }

  async function delMeasurement(RowKey) {
    if (!window.confirm("Weet je het zeker?")) return;
    try {
      await MeasurementService.deleteMeasurement(RowKey);
      loadMeasurements();
    } catch (error) {
      console.error("Error deleting measurement:", error);
      alert("Couldn't remove measurement, please try again");
    }
  }

  const onSubmit = async (data) => {
    // Convert form data to proper types
    const measurementData = {
      Measurement1: parseInt(data.Measurement1),
      Measurement2: parseInt(data.Measurement2),
      DurationMinutes: parseInt(data.DurationMinutes),
      MicronsPerMinute: parseFloat(
        parseFloat(
          (parseInt(data.Measurement1) - parseInt(data.Measurement2)) /
            parseInt(data.DurationMinutes)
        ).toFixed(4)
      ),
      Timestamp: Date.now(),
      RowKey: calculateReverseTicks(Date.now()),
    };

    try {
      await MeasurementService.addMeasurement(measurementData);
      reset();
      loadMeasurements();
    } catch (error) {
      console.error("Error adding measurement:", error);
      alert("Couldn't submit data, please try again");
    }
  };

  const getTicks = (start, end) => {
    const diffHours = differenceInHours(end, start);
    const interval = 30 * 60 * 1000; // 30 min interval
    const start2 = parseInt(start / (60 * 60 * 1000)) * 60 * 60 * 1000;
    const current = start2;
    const num = diffHours * 2 + 4;
    const ticksT = [];
    for (let i = 1; i < num - 1; i++) {
      ticksT.push(current + i * interval);
    }
    return ticksT;
  };

  const ticks =
    metingen.length &&
    metingen[0].MeasurementTimestamp &&
    metingen[metingen.length - 1].MeasurementTimestamp
      ? getTicks(
          metingen[metingen.length - 1].MeasurementTimestamp,
          metingen[0].MeasurementTimestamp
        )
      : [1, 2, 3, 4, 5];

  // async function downloadData() {
  //   try {
  //     const mdata = await MeasurementService.getAllMeasurements();
  //     const data = mdata.map((m) => {
  //       let timestampStr = "Invalid Date";
  //       if (m.timestamp) {
  //         const date = new Date(m.timestamp);
  //         if (!isNaN(date.getTime())) {
  //           timestampStr = format(date, "d-M-yyyy hh:mm");
  //         }
  //       }
  //       return `${timestampStr};\
  //         ${m.measurement1};\
  //         ${m.measurement2};\
  //         ${m.durationMinutes};\
  //         ${m.micronsPerMinute};\
  //         \n`;
  //     });
  //     let csvContent =
  //       "data:text/csv;charset=utf-8,Moment;Meting1;Meting2;Duur;MicronPerMin\n" +
  //       data.join("");

  //     console.log(csvContent);
  //     var encodedUri = encodeURI(csvContent);

  //     var link = document.createElement("a");
  //     link.setAttribute("href", encodedUri);
  //     link.setAttribute("download", "data.csv");
  //     // document.body.appendChild(link); // Required for FF

  //     link.click(); // This will download the data file named "my_data.csv".
  //   } catch (error) {
  //     console.error("Error downloading data:", error);
  //     alert("Kon gegevens niet downloaden. Probeer het opnieuw.");
  //   }
  // }

  return (
    <React.Fragment>
      <h3 className="mb-4 mt-5">Metingen</h3>
      <Card className="my-4 mx-auto" as={Col} sm={8} bg="light">
        <Card.Body>
          <Card.Title>Nieuwe meting</Card.Title>
          <Form autoComplete="no" onSubmit={handleSubmit(onSubmit)}>
            <Form.Group as={Row}>
              <Form.Label as={Col} className="mt-1">
                1e meting:
              </Form.Label>
              <Col>
                <Form.Control
                  name="Measurement1"
                  type="number"
                  className={errors.Measurement1 ? "is-invalid" : ""}
                  placeholder="in micron"
                  ref={register({
                    required: true,
                    min: 1,
                    max: 2000,
                  })}
                />
                <Form.Control.Feedback type="invalid">
                  Vereist. Min 1, max 2000 micron.
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Form.Label as={Col} className="mt-1">
                2e meting:
              </Form.Label>
              <Col>
                <Form.Control
                  name="Measurement2"
                  type="number"
                  className={errors.measurement2 ? "is-invalid" : ""}
                  placeholder="in micron"
                  ref={register({
                    required: true,
                    min: 1,
                    max: 2000,
                    validate: (value) =>
                      parseInt(getValues().Measurement1) > parseInt(value),
                  })}
                />
                <Form.Control.Feedback type="invalid">
                  Vereist. Min 1, max 2000 micron. Kleiner dan meting 1.
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Form.Label as={Col} className="mt-1">
                Metingduur:
              </Form.Label>
              <Col>
                <Form.Control
                  name="DurationMinutes"
                  type="number"
                  placeholder="in min."
                  className={errors.DurationMinutes ? "is-invalid" : ""}
                  ref={register({
                    required: true,
                    min: 2,
                    max: 15,
                  })}
                />
                <Form.Control.Feedback type="invalid">
                  Vereist. Min 2, max 15 minuten.
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit">
                Opslaan
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <h5>Metingen</h5>
      <ResponsiveContainer width="100%" height={200} className="my-4">
        <ScatterChart data={metingen} barSize={10} style={{ marginLeft: -25 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <YAxis />
          <XAxis
            dataKey="MeasurementTimestamp"
            scale="time"
            ticks={ticks}
            domain={["auto", "auto"]}
            type="number"
            tickFormatter={(timeStr) => {
              if (!timeStr || timeStr === null || timeStr === undefined) {
                return "00:00";
              }
              const date = new Date(timeStr);
              if (isNaN(date.getTime())) {
                return "00:00";
              }
              return format(date, "HH:mm");
            }}
          />
          <Legend />
          <Scatter dataKey="MicronsPerMinute" fill={config.eloxOrange} />
        </ScatterChart>
      </ResponsiveContainer>

      <Table responsive bordered hover size="sm">
        <thead>
          <tr>
            <th>Moment</th>
            <th>M1</th>
            <th>M2</th>
            <th>Duur</th>
            <th>Micron/min.</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {metingen.map((m) => (
            <tr key={m.RowKey}>
              <td>
                {m.MeasurementTimestamp
                  ? (() => {
                      const date = new Date(m.MeasurementTimestamp);
                      if (isNaN(date.getTime())) {
                        return "Invalid Date";
                      }
                      return format(date, "dd LLL HH:mm");
                    })()
                  : "No Date"}
              </td>
              <td>{m.Measurement1}</td>
              <td>{m.Measurement2}</td>
              <td>{m.DurationMinutes}</td>
              <td>{parseFloat(m.MicronsPerMinute).toFixed(4)}</td>
              <td>
                <Button
                  onClick={() => delMeasurement(m.RowKey)}
                  className="m-0 p-0"
                  variant="link"
                >
                  <TrashFill style={{ color: "red" }} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {/* <Button onClick={() => downloadData()}>
        Alle meethistorie downloaden (CSV)
      </Button> */}
      <br />
      <br />
    </React.Fragment>
  );
};

export default Metingen;
